import { Typography } from '@mui/material'
import type { KickPlateFixingHoles, Model } from 'metalworks-domain'
import { ApplicationTypes, CountersinkHole, KickPlate, getRoundShapeAnchorPointRecipes } from 'metalworks-domain'
import { useObservableEagerState } from 'observable-hooks'
import { useEffect, useMemo, useState } from 'react'

import { isNonNullish } from '@salsita-ai/platform-common/utils'
import { FixDialog } from '@salsita-ai/platform-project-sdk/ui'

import { configuratorInterface } from '../systems/configurator-interface'

// TODO: this should be moved to the sdk and generalized with parameters for fixes - check SampleDomainFix for inspiration
export const AppInvariantViolations = () => {
  // TODO: this works only if there is just single fix => how to handle multiple fixes at the same time? Maybe we don't want to?
  const [suppressed, setSuppressed] = useState(false)

  const { navigationSystem, storeSystem } = configuratorInterface.useDomain()

  const $plate = configuratorInterface.useRootEntityState()
  const potentiallyInvalid = useObservableEagerState(storeSystem.stateSlices.potentiallyInvalid)

  const violations = useMemo(() => {
    if (potentiallyInvalid.validity.severity !== 'fatal' && potentiallyInvalid.validity.severity !== 'warning') {
      return []
    }

    return potentiallyInvalid.validity.entityViolations
  }, [potentiallyInvalid])

  useEffect(() => {
    setSuppressed(false)
  }, [violations])

  const firstViolation = violations[0]

  if (potentiallyInvalid.kind === 'reset') {
    return
  }

  if (firstViolation?.violation.type === 'kickPlateMountingHoleBigEnough') {
    // This applies only to Kick Plates - assume just one hole.
    const currentHole = $plate.$holes[0] as KickPlateFixingHoles
    if (!currentHole) {
      return null
    }

    return (
      <FixDialog
        title="Autofix"
        onFix={() => {
          storeSystem.stageAndCommit([
            configuratorInterface.updates.DomainEntityReplacement({
              entityId: currentHole.id as any,
              replacementEntity: {
                id: `KickPlateBoltHoles-1` as Model.KickPlateBoltHoles['id'],
                type: 'KickPlateBoltHoles',
                anchorPointFromId: currentHole.anchorPointFromId,
                anchorPointToId: currentHole.anchorPointToId,
                horizontalCount: currentHole.horizontalCount,
                verticalCount: currentHole.verticalCount,
                x: currentHole.x,
                y: currentHole.y,
                radius: $plate.$powderCoatingMinHoleSize / 2,
              },
              keepChildren: true,
              updateTag: 'fix.replaceKickPlateHole',
            }),
          ])
        }}
        onClose={() => {
          storeSystem.stageAndCommit([
            configuratorInterface.updates.DomainEntityModification({
              entityId: $plate.id,
              updateTag: 'fix.disablePowderCoating',
              stateDelta: { powderCoating: false },
            }),
          ])
          setSuppressed(true)
        }}
        open={!suppressed}
        cancelButtonTitle="Disable powder coating"
        fixButtonTitle="Change"
      >
        <Typography>
          Powder Coating needs a {$plate.$powderCoatingMinHoleSize}mm hole, these countersinks are too small. Do you
          want to change to {$plate.$powderCoatingMinHoleSize}mm bolt holes instead?
        </Typography>
      </FixDialog>
    )
  }

  if (firstViolation?.violation.type === 'powderCoatingHoleMultipleAvailable' && $plate.powderCoatingMountingHoleId) {
    const powderCoatingMountingHoleId = $plate.powderCoatingMountingHoleId

    return (
      <FixDialog
        title="Autofix"
        onFix={() => {
          storeSystem.stageAndCommit([
            configuratorInterface.updates.EntityDeletion({
              entityId: powderCoatingMountingHoleId,
              updateTag: 'fix.deletePowderCoatingHole',
            }),
          ])
        }}
        onClose={() => {
          storeSystem.stageAndCommit([
            configuratorInterface.updates.DomainEntityModification({
              entityId: $plate.id,
              updateTag: 'fix.unmarkPowderCoatingHoleAsMountingHole',
              stateDelta: { powderCoatingMountingHoleId: null },
            }),
          ])
          setSuppressed(true)
        }}
        open={!suppressed}
        cancelButtonTitle="Keep"
        fixButtonTitle="Remove"
      >
        <Typography>
          Automatically added mounting hole for powder coating is not needed anymore, do you want to remove it?
        </Typography>
      </FixDialog>
    )
  }

  if (firstViolation?.violation.type === 'powderCoatingHoleAvailable') {
    const isKickPlate = KickPlate.is($plate)

    return (
      <FixDialog
        title="Add Mounting Hole"
        onFix={() => {
          const idSequence = configuratorInterface.getIdSequence(
            storeSystem.stateSlices.committed.value.state.entities,
            []
          )

          const baseHoleProps = isKickPlate
            ? {
                id: idSequence.nextId('KickPlateBoltHoles') as Model.KickPlateBoltHoles['id'],
                type: 'KickPlateBoltHoles' as const,
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                anchorPointToId: $plate.$anchorPoints.find(ap => ap.position === 'bottomLeft')?.id!,
                horizontalCount: -1,
                verticalCount: -1,
              }
            : {
                id: idSequence.nextId('BoltHole') as Model.BoltHole['id'],
                type: 'BoltHole' as const,
                // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                anchorPointToId: $plate.$anchorPoints.find(ap => ap.position === 'center')?.id!,
              }

          const newAnchorPointId = idSequence.nextId('AnchorPoint') as Model.AnchorPoint['id']
          const newAnchorPointIdNumber = Number(newAnchorPointId.split('-').pop())
          const anchorPointRecipes = getRoundShapeAnchorPointRecipes(baseHoleProps.id, newAnchorPointIdNumber)
          const newAnchorPoint = anchorPointRecipes[0]!

          const entity = {
            ...baseHoleProps,
            radius: $plate.$powderCoatingMinHoleSize / 2,
            x: 0,
            y: 0,
            anchorPointFromId: newAnchorPoint.id,
          }

          if (isKickPlate) {
            const featureSettings = $plate.$featureSettings.find(
              fs => fs.plateType === $plate.type && fs.featureType === entity.type
            )

            Object.assign(entity, {
              x: featureSettings?.offsetX ?? 20,
              y: featureSettings?.offsetY ?? 20,
            })
          }

          storeSystem.stageAndCommit([
            configuratorInterface.updates.DomainEntityCreation({
              entity,
              updateTag: 'fix.addPowderCoatingMountingHole',
              parentId: $plate.id,
            }),
            configuratorInterface.updates.DomainEntityModification({
              entityId: $plate.id,
              updateTag: 'fix.addPowderCoatingMountingHole',
              stateDelta: {
                powderCoatingMountingHoleId: entity.id,
              },
            }),
            ...anchorPointRecipes.map(anchorPoint =>
              configuratorInterface.updates.DomainEntityCreation({
                entity: anchorPoint,
                updateTag: 'fix.addPowderCoatingMountingHole',
                parentId: entity.id,
              })
            ),
            navigationSystem.getNavigationUpdate(ApplicationTypes.NavigationState.Features),
            navigationSystem.getSelectUpdate(entity.id),
          ])
        }}
        onClose={() => {
          storeSystem.stageAndCommit([
            configuratorInterface.updates.DomainEntityModification({
              entityId: $plate.id,
              updateTag: 'fix.disablePowderCoating',
              stateDelta: { powderCoating: false },
            }),
          ])
          setSuppressed(true)
        }}
        open={!suppressed}
        cancelButtonTitle="Disable powder coating"
        fixButtonTitle="Fix"
      >
        <Typography>
          {isKickPlate
            ? `Powder coating needs a ${$plate.$powderCoatingMinHoleSize}mm hole. Do you want to automatically add 6mm holes?`
            : `Powder coating needs a ${$plate.$powderCoatingMinHoleSize}mm hole. Do you want to automatically add a ${$plate.$powderCoatingMinHoleSize}mm hole?`}
        </Typography>
      </FixDialog>
    )
  }

  if (firstViolation?.violation.type === 'countersinkNotAvailable') {
    return (
      <FixDialog
        title="Autofix"
        onFix={() => {
          const updates = potentiallyInvalid.state.entities
            .map(e => {
              if (CountersinkHole.is(e)) {
                return configuratorInterface.updates.EntityDeletion({
                  entityId: e.id,
                  updateTag: 'fix.removeCountersink',
                })
              }

              return null
            })
            .filter(isNonNullish)

          storeSystem.stageAndCommit([...potentiallyInvalid.updates, ...updates])
        }}
        onClose={() => {
          setSuppressed(true)
        }}
        open={!suppressed}
        cancelButtonTitle="Keep"
        fixButtonTitle="Remove"
      >
        <Typography>
          Some of the countersinks are not available for the selected plate. Do you want to remove them from the plate?
        </Typography>
      </FixDialog>
    )
  }

  return null
}
